import React from 'react';

import type { AiChatReference } from '~/components/AiChat/types';
import Chip from '~/components/core/Atomic/Chip/Chip';
import Text from '~/components/core/TextComponents/Text';
import { serverDateTimeToLocalMoment } from '~/DateTimeUtils';

import { TEXTS } from './texts';

/**
 * ExpertReferencePreview
 *
 * The hover preview of a cited expert report: its name and version date, a note when the report has
 * been revised since that version, and a hint to click for the full report.
 */

/** The date of the report version a citation names, in the user's time zone. */
export const getVersionDate = (reference: AiChatReference): string | undefined =>
  reference.version_at ? serverDateTimeToLocalMoment(reference.version_at).format('lll') : undefined;

interface ExpertReferencePreviewProps {
  reference: AiChatReference;
}

export const ExpertReferencePreview: React.FC<ExpertReferencePreviewProps> = ({ reference }) => {
  const versionDate = getVersionDate(reference);

  return (
    <div className="flex max-w-[400px] flex-col items-start gap-4 rounded-lg p-12">
      <div className="flex w-full min-w-0 flex-row items-center gap-4">
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="min-w-0 flex-1 truncate">
          {reference.name ?? ''}
        </Text>
        {reference.is_latest === false && (
          <Chip
            size="small"
            label={TEXTS.NEWER_VERSION_EXISTS}
            className="h-20 flex-shrink-0 rounded-full bg-orange-100 text-orange-700"
          />
        )}
      </div>
      {versionDate && (
        <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {versionDate}
        </Text>
      )}
      {reference.text && (
        <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.CLICK_TO_READ_REPORT}
        </Text>
      )}
    </div>
  );
};
